<!--
  The whole coach-review screen: header, card and button bar. The check-up being reviewed
  is always one the client has already submitted, so the header never shows a step counter
  and never uses the wizard's full-width layout.

  While the coaching dashboard switch is on (#5456) the header carries tabs. The Decision
  content is hidden, never removed, while another tab shows, so a number the coach typed
  survives a look at the Body Composition or Training tab. The button bar shows on every tab.
-->
<cs-header [backButton]="true" [fullWidth]="false" [separator]="true">
  <cs-header-title centered>
    {{ LL.coach.checkup.reviewTitle() }}
  </cs-header-title>
  @if (coachingDashboardOn) {
  <cs-header-content>
    <cs-segment class="review-tabs" [selection]="tab" (selectionChange)="onTabPicked($event)">
      <cs-segment-button [value]="CoachReviewTab.Decision">
        {{ LL.coach.checkup.tabDecision() }}
      </cs-segment-button>
      <cs-segment-button [value]="CoachReviewTab.BodyComposition">
        {{ LL.coach.checkup.tabBodyComposition() }}
      </cs-segment-button>
      <cs-segment-button [value]="CoachReviewTab.Training">
        {{ LL.trainingOverview.tabTraining() }}
      </cs-segment-button>
    </cs-segment>
  </cs-header-content>
  }
</cs-header>

<cs-content>
  <cs-card-list class="decision-tab" [hidden]="tab !== CoachReviewTab.Decision">
    <cs-card>
      <!-- #6676: with the switch on the header already says "Review Checkup", so the card opens with the lead -->
      @if (!coachingDashboardOn) {
      <cs-card-title>
        {{ LL.coach.checkup.reviewTitle() }}
      </cs-card-title>
      }
      <cs-card-content>
        <cs-note big light>
          {{ LL.coach.checkup.reviewActionPrompt() }}
        </cs-note>
        @if (coachingDashboardOn) {
        <!-- #5456 review round: the weight change and body fat stay above the intake card as context, drawn
             as the overview draws its measurements (#6676): the change in the weight colour, never by the goal -->
        @if (weightChange || checkup.bfData?.bf_pc) {
        <div class="measurement-boxes decision-measurements">
          @if (weightChange) {
          <div class="measurement-box weight-change">
            <div class="measurement" [style.--change-color]="'var(' + weightChange.color + ')'">
              <div class="measurement-label">{{ LL.coach.checkup.weightChange() }}</div>
              <div class="measurement-value">
                <span class="current-value">{{ weightChange.valueText }}</span>
                <span class="change-text">
                  @if (weightChange.changeText) {
                  ({{ weightChange.changeText }})
                  }
                  @if (weightChange.trendIcon) {
                  <cs-icon [name]="weightChange.trendIcon"></cs-icon>
                  }
                </span>
              </div>
              @if (weightChange.perWeek; as perWeek) {
              <div class="measurement-change">
                <span class="change-value">{{ perWeek.lead }}</span>
                <span class="change-label">{{ perWeek.rest }}</span>
              </div>
              }
            </div>
          </div>
          }
          @if (checkup.bfData?.bf_pc) {
          <div class="measurement-box estimated-bodyfat">
            <div class="measurement">
              <div class="measurement-label">{{ LL.weeklyCheckup.labels.estimatedBodyfat() }}</div>
              <div class="measurement-value">
                <span class="current-value">{{ LL.coach.checkup.bfPercent({ value: checkup.bfData.bf_pc }) }}</span>
              </div>
            </div>
          </div>
          }
        </div>
        }
        } @else {
        <cs-item-list>
          <div class="status-section reported-intake">
            <cs-label class="section-label">{{ LL.coach.checkup.currentTarget() }}</cs-label>
            <div class="section-value">
              <span class="value-text">{{ LL.coach.checkup.kcalValue({ value: currentTargetDisplay() }) }}</span>
            </div>
          </div>
          <div class="status-section recommended-target">
            <cs-label class="section-label">{{ LL.coach.checkup.recommendedTarget() }}</cs-label>
            <div class="section-value">
              <span class="value-text">{{ LL.coach.checkup.kcalValue({ value: recommendedTargetDisplay() }) }} ({{ recommendedDeltaDisplay() }})</span>
            </div>
          </div>
          @if (checkup.bfData?.bf_pc) {
          <div class="status-section">
            <cs-label class="section-label">{{ LL.weeklyCheckup.labels.estimatedBodyfat() }}</cs-label>
            <div class="section-value">
              <span class="value-text">{{ LL.coach.checkup.bfPercent({ value: checkup.bfData.bf_pc }) }}</span>
            </div>
          </div>
          }
        </cs-item-list>

        @if (reviewMode === 'modify') {
        <cs-item-list>
          <cs-item-group>
            <cs-note>{{ LL.coach.checkup.customCalories() }}</cs-note>
            <cs-input-measure #coachModifyInput [(canonValue)]="customCalories"
              type="nutrient_kcal"></cs-input-measure>
          </cs-item-group>
        </cs-item-list>
        }
        }
      </cs-card-content>
    </cs-card>

    @if (coachingDashboardOn) {
    <!-- #5456: the intake and the coach's answer, a card of its own since #6676. The answers stay radio
         rows: the bar's Confirm sends the picked one, so the card must not carry buttons of its own -->
    <cs-next-intake-card class="decision-card" [nowKcal]="checkup.calorieIntakeBaseline"
      [nowSource]="checkup.selectedCalorieIntakeBaseline" [audience]="CalorieChangeAudience.Coach" [recommendedKcal]="checkup.recommendedAvgCalories"
      [recommendsChange]="recommendedTargetDisplay() !== currentTargetDisplay()">
      <cs-radio-group class="decision-choices" [selected]="decision" (selectedChange)="onDecisionPicked($event)">
        @for (choice of decisionChoices; track choice) {
        <cs-radio [value]="choice" [attr.data-choice]="choice" [disabled]="isInFlight">
          {{ decisionLabel(choice) }}
          <span item-right class="choice-kcal">{{ decisionKcal(choice) }}</span>
          <cs-note light>{{ decisionHelp(choice) }}</cs-note>
        </cs-radio>
        @if (choice === CoachResolvedAction.Modified && decision === CoachResolvedAction.Modified) {
        <cs-item-list class="coach-target-fields">
          <cs-calorie-change-input #coachCalorieChange [baseCalories]="checkup.calorieIntakeBaseline"
            [minimumDailyIntake]="minimumDailyIntake()" [defaultDirection]="defaultChangeDirection()"
            [audience]="CalorieChangeAudience.Coach" [(calories)]="customCalories"></cs-calorie-change-input>
        </cs-item-list>
        }
        }
      </cs-radio-group>
    </cs-next-intake-card>
    }
  </cs-card-list>

  <!-- the Body Composition tab: drawn once the coach has opened it, then kept and only hidden -->
  @if (coachingDashboardOn && overviewRequested) {
  <cs-card-list class="body-composition-tab" [hidden]="tab !== CoachReviewTab.BodyComposition">
    <cs-body-comp-overview [model]="overviewModel" [loading]="overviewLoading" [failed]="overviewFailed"
      (retry)="loadOverviewAsync()">
    </cs-body-comp-overview>
  </cs-card-list>
  }

  <!-- the Training tab (phase 3): the client's Training Overview up to today, drawn once the coach
       has opened it, then kept and only hidden. A tap on an exercise opens the same menu as on the page -->
  @if (coachingDashboardOn && trainingRequested) {
  <!-- a plain column, not a cs-card-list: that list places only cards, and drew the head line beside the grid (#6675) -->
  <div class="training-tab" [hidden]="tab !== CoachReviewTab.Training">
    <div class="training-tab-head">
      <cs-note light class="up-to-today">{{ LL.trainingOverview.upToToday() }}</cs-note>
      <cs-button small secondary [label]="LL.trainingOverview.openOverview()" (click)="onOpenTrainingOverviewClick($event)"></cs-button>
    </div>
    <cs-training-overview [model]="trainingModel" [busy]="trainingLoading" [failed]="trainingFailed"
      [canShowEarlier]="trainingButtons.canShowEarlier" [canShowLater]="trainingButtons.canShowLater"
      [loadedWeeks]="trainingPaging.windowDays / 7" (retry)="loadTrainingAsync()" (rowTap)="onTrainingRowTap($event)"
      (showEarlier)="onTrainingShowEarlier($event)" (showLater)="onTrainingShowLater($event)">
    </cs-training-overview>
  </div>
  }
</cs-content>

<cs-page-action-buttons>
  @if (coachingDashboardOn) {
  <!-- #5456: one button; on another tab with no valid answer picked it brings the Decision tab forward -->
  <cs-button medium primary class="confirm-decision" [label]="LL.common.buttons.confirm()"
    [disabled]="isConfirmDisabled()" (click)="onConfirmClick($event)">
  </cs-button>
  } @else if (reviewMode === 'summary') {
  <cs-button medium secondary [label]="LL.coach.checkup.decline()" (click)="onDeclineClick($event)">
  </cs-button>
  <cs-button medium secondary [label]="LL.coach.checkup.modify()" (click)="onModifyClick($event)">
  </cs-button>
  <cs-button medium primary [label]="LL.coach.checkup.accept()" (click)="onAcceptClick($event)">
  </cs-button>
  } @else {
  <cs-button medium secondary [label]="LL.common.buttons.cancel()" (click)="onCancelModifyClick($event)">
  </cs-button>
  <cs-button medium primary [label]="LL.common.buttons.apply()" [disabled]="!customCalories || customCalories <= 0"
    (click)="onApplyModifyClick($event)">
  </cs-button>
  }
</cs-page-action-buttons>
